import type { CSSProperties, FunctionComponent, ReactNode } from "react";
import { cn } from "./cn";

export interface DescriptionsItem {
  key?: string;
  label: ReactNode;
  children?: ReactNode;
}

export interface DescriptionsProps {
  items: readonly (DescriptionsItem | false | null | undefined)[];
  /** Label/content pairs per row. The last item stretches to fill its row, as in antd. */
  column?: number;
  bordered?: boolean;
  size?: "small" | "middle";
  /**
   * `horizontal`: label beside content. `vertical`: label above content, one item per row.
   * `responsive`: vertical below `sm`, horizontal from `sm` up.
   */
  layout?: "horizontal" | "vertical" | "responsive";
  className?: string;
}

/** Read-only label/value grid (antd `Descriptions`) on a `<dl>`. */
export const Descriptions: FunctionComponent<DescriptionsProps> = ({
  items,
  column = 3,
  bordered,
  size = "middle",
  layout = "horizontal",
  className,
}) => {
  const visible = items.filter((item): item is DescriptionsItem => Boolean(item));
  // Each item is two grid tracks (label + content); the last one spans what its row has left.
  const lastSpan = 1 + 2 * (column - 1 - ((visible.length - 1) % column));
  const horizontalColumns = `repeat(${column}, max-content minmax(0, 1fr))`;

  const gridClass = {
    horizontal: "[grid-template-columns:var(--descriptions-columns)]",
    vertical: "grid-cols-1",
    responsive: "grid-cols-1 sm:[grid-template-columns:var(--descriptions-columns)]",
  }[layout];
  const lastSpanClass = {
    horizontal: "[grid-column:var(--descriptions-last-span)]",
    vertical: "",
    responsive: "sm:[grid-column:var(--descriptions-last-span)]",
  }[layout];

  const cellPadding = size === "small" ? "px-4 py-2" : "px-6 py-3";
  const cellBorder = bordered && "border-e border-b border-noa";

  return (
    <dl
      className={cn(
        "m-0 grid text-sm",
        gridClass,
        bordered && "overflow-hidden rounded-lg border-s border-t border-noa",
        className
      )}
      style={
        {
          "--descriptions-columns": horizontalColumns,
          "--descriptions-last-span": `span ${lastSpan} / span ${lastSpan}`,
        } as CSSProperties
      }
    >
      {visible.map((item, index) => {
        const isLast = index === visible.length - 1;
        return [
          <dt
            key={`${item.key ?? index}-label`}
            className={cn(
              "text-noa",
              bordered ? cn(cellPadding, cellBorder, "bg-noa-hover") : "pe-2 pb-3 text-noa-muted"
            )}
          >
            {item.label}
          </dt>,
          <dd
            key={`${item.key ?? index}-content`}
            className={cn(
              "m-0 min-w-0 break-words text-noa",
              bordered ? cn(cellPadding, cellBorder, "bg-noa") : "pe-4 pb-3",
              isLast && lastSpanClass
            )}
          >
            {item.children}
          </dd>,
        ];
      })}
    </dl>
  );
};
